import { Alert, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import React, { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../api/axiosInstance";
import { useAuth } from "../auth/AuthContext";
import { PublicFormButton, PublicFormCard } from "../components/common/form/PublicFormCard";
import { APIS } from "../modules/common/api/apis";
import { saveLoggedInUserData } from "../modules/common/user/UserService";
import { UserRole, UserRoleValue } from "../auth/roles";
import { getApiErrorMessage } from "../modules/common/shared/util/ApiErrorUtil";

const SUPPORTED_ROLES: string[] = [UserRole.ADMIN, UserRole.FACULTY, UserRole.STUDENT];

export const LoginPage: React.FC = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ uName: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateField = (field: "uName" | "password", value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage(null);
  };

  const login = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setErrorMessage(null);
    setIsSubmitting(true);
    const uName = formData.uName.trim();
    try {
      const response = await axiosInstance.post(APIS.LOGIN, { userName: uName, password: formData.password });
      const role: string | undefined = response?.data?.authorities?.[0];
      if (!role || !SUPPORTED_ROLES.includes(role)) {
        setErrorMessage("Your account doesn't have access to this application. Please contact the school office.");
        return;
      }
      saveLoggedInUserData(response?.data);
      // the backend omits empty lists, so no grades arrives as undefined
      const grades: string[] = response?.data?.userDTO?.grades ?? [];
      setUser({ name: uName, role: role as UserRoleValue, grades });
      const stateData =
        role === UserRole.ADMIN
          ? { loginMessage: "Welcome From Login" }
          : { loginMessage: "Welcome From Login", grades: response?.data?.userDTO?.grades };
      navigate("/app/dashboard", { replace: true, state: stateData });
    } catch (err) {
      if (axios.isAxiosError(err) && (err.response?.data === "INVALID_CREDENTIALS" || err.response?.status === 401)) {
        setErrorMessage("Invalid username or password.");
      } else {
        setErrorMessage(getApiErrorMessage(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicFormCard icon={<LockRoundedIcon />} title="Welcome back" subtitle="Sign in to continue to your account." maxWidth={420}>
      <Stack component="form" spacing={2} onSubmit={login}>
        {errorMessage && (
          <Alert severity="error" role="alert" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}
        <TextField
          fullWidth
          label="User Name"
          name="userName"
          autoComplete="username"
          autoFocus
          value={formData.uName}
          onChange={(e) => updateField("uName", e.target.value)}
          required
          size="small"
          disabled={isSubmitting}
        />
        <TextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          value={formData.password}
          onChange={(e) => updateField("password", e.target.value)}
          required
          size="small"
          disabled={isSubmitting}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((s) => !s)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <PublicFormButton type="submit" loading={isSubmitting}>
          Sign in
        </PublicFormButton>
      </Stack>
    </PublicFormCard>
  );
};
